import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getIntegrationBySlug,
  getCategoryBySlug,
  getRelatedIntegrations,
  getIntegrationNeighbors,
  getIntegrationsByCategory,
  integrations,
  integrationsContent,
  site,
} from "@/content";
import LightNavbar from "@/pages/main-light/components/LightNavbar";
import LightFooter from "@/pages/main-light/components/LightFooter";
import IntegrationLeadForm from "@/pages/integrations/components/IntegrationLeadForm";
import SeoHead from "@/components/feature/SeoHead";
import { RichTemplate, interpolate } from "@/lib/rich";
import { useSurfaces } from "@/theme/surfaces";
import NotFound from "@/pages/NotFound";

/**
 * Страница одной интеграции. Адрес один (`/integrations/:slug`) —
 * тема переключается без смены URL, разметка общая.
 */
export default function IntegrationDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const integration = slug ? getIntegrationBySlug(slug) : undefined;
  const labels = integrationsContent.detail;
  const t = useSurfaces();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!integration) {
    return <NotFound />;
  }

  const category = getCategoryBySlug(integration.category);
  const related = getRelatedIntegrations(integration);
  const neighbors = getIntegrationNeighbors(integration);
  const siblings = getIntegrationsByCategory(integration.category);

  // Заголовок собирается по правилу из контента — тексты не в коде
  const rule = labels.headingRule;
  const heading = integration.title.includes(rule.alreadyIntegratedPrefix)
    ? integration.title.replace(rule.alreadyIntegratedPrefix, rule.integratedReplacement)
    : integration.title.includes(rule.containsMarker)
    ? integration.title
    : interpolate(rule.defaultTemplate, { title: integration.title });

  return (
    <>
      <SeoHead
        title={`${integration.title} — интеграция с amoCRM | Быстренко.ру`}
        description={`${integration.shortDesc} ${integration.price}, срок ${integration.duration}. Настройка интеграции ${integration.title} с amoCRM под ключ.`}
        keywords={`интеграция ${integration.title}, ${integration.title} amoCRM, подключить ${integration.title} к CRM, ${integration.title} интеграция`}
        canonicalPath={`/integrations/${integration.slug}`}
      />

      <div className={t.page}>
        <LightNavbar />

        {/* Hero */}
        <section className="pt-32 pb-12 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className={`absolute top-[10%] right-[5%] w-[480px] h-[480px] rounded-full blur-[140px] ${t.glowA}`} />
            <div className={`absolute bottom-[0%] left-[-5%] w-[380px] h-[380px] rounded-full blur-[130px] ${t.glowB}`} />
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <nav className="flex items-center gap-2 text-sm mb-8 whitespace-nowrap overflow-x-auto">
              <Link to="/" className={`transition-colors cursor-pointer ${t.crumbLink}`}>
                {site.breadcrumbs.home}
              </Link>
              <i className={`ri-arrow-right-s-line ${t.crumbSep}`}></i>
              <Link
                to="/integrations"
                className={`transition-colors cursor-pointer ${t.crumbLink}`}
              >
                {site.breadcrumbs.integrations}
              </Link>
              <i className={`ri-arrow-right-s-line ${t.crumbSep}`}></i>
              <span className={`font-medium ${t.crumbCurrent}`}>
                {integration.title}
              </span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-16">
              <div className="flex-1 max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 ${t.categoryIconBox}`}
                  >
                    <i
                      className={`${integration.icon} text-xl ${t.categoryIcon}`}
                    ></i>
                  </div>
                  {category && (
                    <Link
                      to={`/integrations#${category.slug}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${t.chip}`}
                    >
                      <i className={`${category.icon} text-sm`}></i>
                      {category.title}
                    </Link>
                  )}
                </div>

                <h1
                  className={`text-3xl md:text-5xl font-extrabold leading-[1.12] mb-5 ${t.h1}`}
                >
                  {heading}
                </h1>

                <p className={`text-lg leading-relaxed mb-8 ${t.body}`}>
                  {integration.desc}
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-8">
                  <div
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${t.metaCard}`}
                  >
                    <i className={`ri-money-ruble-circle-line ${t.accent}`}></i>
                    <span className={`font-bold text-sm ${t.h3}`}>
                      {integration.price}
                    </span>
                  </div>
                  <div
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${t.metaCard}`}
                  >
                    <i className={`ri-time-line ${t.muted}`}></i>
                    <span className={`font-medium text-sm ${t.metaText}`}>
                      {integration.duration}
                    </span>
                  </div>
                  <div
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${t.metaCard}`}
                  >
                    <i className="ri-shield-check-line text-emerald-600"></i>
                    <span className={`font-medium text-sm ${t.metaText}`}>
                      {labels.guaranteeLabel}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#lead"
                    className={`px-7 py-3.5 rounded-xl font-bold text-sm transition-colors cursor-pointer text-center whitespace-nowrap ${t.ctaPrimary}`}
                  >
                    {labels.orderCta}
                  </a>
                  <Link
                    to="/integrations"
                    className={`px-7 py-3.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer text-center whitespace-nowrap ${t.ctaSecondary}`}
                  >
                    {labels.allCta}
                  </Link>
                </div>
              </div>

              {/* Боковая карточка */}
              <aside className="w-full lg:w-[380px] shrink-0">
                <div className={`rounded-3xl p-6 ${t.panel}`}>
                  <div
                    className={`font-bold text-sm mb-4 uppercase tracking-wider ${t.panelTitle}`}
                  >
                    {labels.benefitsTitle}
                  </div>
                  <ul className="space-y-3 mb-6">
                    {labels.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-2.5">
                        <span
                          className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5 ${t.checkCircle}`}
                        >
                          <i
                            className={`ri-check-line text-[10px] ${t.checkIcon}`}
                          ></i>
                        </span>
                        <span className={`text-sm leading-relaxed ${t.body}`}>
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {integration.industries.length > 0 && (
                    <>
                      <div
                        className={`font-bold text-sm mb-3 uppercase tracking-wider ${t.panelTitle}`}
                      >
                        {labels.industriesTitle}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {integration.industries.map((industry) => (
                          <span
                            key={industry}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${t.industryChip}`}
                          >
                            {industry}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Смежные интеграции */}
        {related.length > 0 && (
          <section className="pb-4">
            <div className="max-w-7xl mx-auto px-6">
              <div className={`rounded-3xl p-6 md:p-8 ${t.panel}`}>
                <h2
                  className={`font-bold text-sm uppercase tracking-wider mb-4 ${t.panelTitle}`}
                >
                  {labels.relatedTitle}
                </h2>
                <div className="flex flex-wrap gap-2.5">
                  {related.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/integrations/${item.slug}`}
                      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${t.relatedChip}`}
                    >
                      <i className={`${item.icon} text-sm`}></i>
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {integration.faq.length > 0 && (
          <section className="py-14">
            <div className="max-w-3xl mx-auto px-6">
              <h2 className={`text-2xl md:text-3xl font-extrabold mb-6 ${t.h2}`}>
                {labels.faqTitle}
              </h2>
              <div className="space-y-3">
                {integration.faq.map((item) => (
                  <details
                    key={item.q}
                    className={`group rounded-2xl overflow-hidden border ${t.faqItemClosed}`}
                  >
                    <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none">
                      <span
                        className={`font-semibold text-sm md:text-base ${t.faqQuestionClosed}`}
                      >
                        {item.q}
                      </span>
                      <span
                        className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 group-open:rotate-45 transition-all ${t.faqToggleClosed}`}
                      >
                        <i
                          className={`ri-add-line text-sm ${t.faqToggleIconClosed}`}
                        ></i>
                      </span>
                    </summary>
                    <div className="px-6 pb-5">
                      <p className={`text-sm leading-relaxed ${t.body}`}>{item.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Заявка */}
        <section id="lead" className="py-16 scroll-mt-24 relative overflow-hidden">
          <div className={`absolute top-[10%] right-[0%] w-[380px] h-[380px] rounded-full blur-[120px] pointer-events-none ${t.glowA}`} />
          <div className={`absolute bottom-[10%] left-[-5%] w-[300px] h-[300px] rounded-full blur-[110px] pointer-events-none ${t.glowB}`} />

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <h2
                  className={`text-2xl md:text-4xl font-extrabold leading-tight mb-5 ${t.h2}`}
                >
                  <RichTemplate
                    template={labels.leadTitleTemplate}
                    values={{ title: integration.title }}
                    accentClassName={t.accent}
                  />
                </h2>
                <p className={`text-lg leading-relaxed mb-8 ${t.body}`}>
                  {labels.leadText}
                </p>

                <div className="flex flex-col gap-4">
                  {[
                    { icon: "ri-time-line", text: labels.responseTime },
                    {
                      icon: "ri-money-ruble-circle-line",
                      text: `${labels.priceLabel}: ${integration.price}`,
                    },
                    {
                      icon: "ri-calendar-check-line",
                      text: `${labels.durationLabel}: ${integration.duration}`,
                    },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${t.bulletCircle}`}
                      >
                        <i className={`${item.icon} text-sm ${t.bulletIcon}`}></i>
                      </div>
                      <span className={`text-sm ${t.bulletText}`}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-full max-w-lg lg:ml-auto">
                <IntegrationLeadForm
                  formName={`integration-${integration.slug}`}
                  integrationTitle={integration.title}
                  title={labels.formTitle}
                  subtitle={interpolate(labels.formSubtitleTemplate, {
                    title: integration.title,
                  })}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Навигация по каталогу */}
        <section className="pb-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {neighbors.prev ? (
                <Link
                  to={`/integrations/${neighbors.prev.slug}`}
                  className={`group rounded-2xl p-5 transition-all cursor-pointer ${t.neighborCard}`}
                >
                  <div
                    className={`text-xs mb-2 flex items-center gap-1.5 ${t.neighborLabel}`}
                  >
                    <i className="ri-arrow-left-line"></i>
                    {labels.prevLabel}
                  </div>
                  <div
                    className={`font-bold text-sm transition-colors ${t.neighborTitle}`}
                  >
                    {neighbors.prev.title}
                  </div>
                </Link>
              ) : (
                <div />
              )}

              {neighbors.next && (
                <Link
                  to={`/integrations/${neighbors.next.slug}`}
                  className={`group rounded-2xl p-5 transition-all cursor-pointer sm:text-right ${t.neighborCard}`}
                >
                  <div
                    className={`text-xs mb-2 flex items-center gap-1.5 sm:justify-end ${t.neighborLabel}`}
                  >
                    {labels.nextLabel}
                    <i className="ri-arrow-right-line"></i>
                  </div>
                  <div
                    className={`font-bold text-sm transition-colors ${t.neighborTitle}`}
                  >
                    {neighbors.next.title}
                  </div>
                </Link>
              )}
            </div>

            {siblings.length > 2 && (
              <div className={`mt-8 pt-8 border-t ${t.divider}`}>
                <div
                  className={`text-xs uppercase tracking-wider font-semibold mb-4 ${t.neighborLabel}`}
                >
                  {interpolate(labels.othersTitleTemplate, {
                    category: category?.title ?? "",
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {siblings
                    .filter((item) => item.slug !== integration.slug)
                    .slice(0, 10)
                    .map((item) => (
                      <Link
                        key={item.slug}
                        to={`/integrations/${item.slug}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${t.othersChip}`}
                      >
                        {item.title}
                      </Link>
                    ))}
                  {siblings.length > 11 && (
                    <Link
                      to="/integrations"
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${t.othersAll}`}
                    >
                      {interpolate(labels.allLinkTemplate, {
                        count: integrations.length,
                      })}
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        <LightFooter />
      </div>
    </>
  );
}
